/**
 * Shared client for the assets-api zip-download endpoint. StreamSaver is used
 * instead of fetch + Blob + downloadFile so the archive never buffers in tab
 * memory — a multi-GB selection would OOM the page. Errors are rethrown without
 * a toast so each caller owns its own messaging (the KB and folder surfaces
 * already do).
 *
 * Promoted to `shared/api` so any feature (project folders, knowledge bases,
 * ...) can stream a zip without crossing a feature boundary.
 */
import streamSaver from 'streamsaver';
import { getWorkspaceId } from '@/entities/workspace';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { ENV } from '@/lib/config/env';
import { i18n } from '@/shared/i18n';

/** One asset entry in a zip manifest. */
export interface ZipDownloadAsset {
  assetId: string;
  archivePath: string;
  /**
   * Declared for parity with `ZipDownloadAssetDto`, but the assets-api pipeline
   * never reads it (only `assets.length` matters). Pass `0` when unknown.
   */
  fileSize: number;
}

/**
 * Mirrors `ZipDownloadRequestDto` in
 * `apps/assets-api/src/assets/zip/zip-download.controller.ts`. Hand-typed
 * because the zip endpoint is excluded from OpenAPI (binary streaming response
 * → @nestjs/swagger declines to register it), so no generated SDK type exists.
 */
export interface ZipDownloadRequestBody {
  folderName?: string;
  assets: ZipDownloadAsset[];
}

const ZIP_DOWNLOAD_PATH = '/api/v1/assets/zip-download';

/** Replaces filesystem-illegal characters so StreamSaver can save the file locally. */
export function sanitizeZipFilename(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    return 'download.zip';
  }

  // eslint-disable-next-line no-control-regex
  const cleaned = trimmed.replace(/[\\/:*?"<>|\x00-\x1F\x7F]/g, '_');
  return `${cleaned}.zip`;
}

/**
 * POSTs the manifest to the zip endpoint and pipes the response straight to
 * disk via StreamSaver. Rethrows on any network/stream error (without toasting)
 * so callers can surface the failure and reset their own UI state.
 */
async function requestZipDownload(manifest: ZipDownloadRequestBody): Promise<Response> {
  const baseUrl = ENV.ASSETS_API_BASE_URL.trim().replace(/\/+$/, '');
  const headers = new Headers({ 'Content-Type': 'application/json' });

  // Mirror the workspace-id header set by `workspace-id.interceptor.ts` so
  // tenant-scoped guards on assets-api see the same workspace selection.
  const workspaceId = getWorkspaceId();
  if (workspaceId) {
    headers.set('x-selected-workspace-id', workspaceId);
  }

  // Mirror local-dev auth that `configure-client.ts` injects — keeps the raw
  // fetch path consistent with the typed axios clients.
  if (ENV.ENABLE_LOCAL_TESTING_AUTHENTICATION && ENV.LOCAL_TOKEN) {
    headers.set('Authorization', ENV.LOCAL_TOKEN);
  }

  return fetchWithAuth(`${baseUrl}${ZIP_DOWNLOAD_PATH}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(manifest),
  });
}

/**
 * POSTs the manifest and returns the zip bytes.
 *
 * The sheet export needs those bytes in memory so it can put the CSV beside the
 * pictures. `streamZipToDisk` never buffers, which is right for a multi-GB folder
 * and wrong for a board's images.
 */
export async function fetchZipBytes(manifest: ZipDownloadRequestBody): Promise<Uint8Array> {
  const response = await requestZipDownload(manifest);

  if (!response.ok) {
    throw new Error(i18n.t('common:api.zipDownload.failed', { status: response.status }));
  }

  return new Uint8Array(await response.arrayBuffer());
}

export async function streamZipToDisk(
  manifest: ZipDownloadRequestBody,
  filename: string,
): Promise<void> {
  const response = await requestZipDownload(manifest);

  if (!response.ok || !response.body) {
    throw new Error(i18n.t('common:api.zipDownload.failed', { status: response.status }));
  }

  const writable = streamSaver.createWriteStream(filename);

  try {
    await response.body.pipeTo(writable);
  } catch (error) {
    // pipeTo aborts the writable on rejection in modern browsers, but call
    // abort() defensively so the SW tears down the partial file in older
    // implementations.
    try {
      await writable.abort(error);
    } catch {
      // Swallow secondary abort errors — the original error is what matters.
    }
    throw error;
  }
}
